import React, { createContext, useContext, useState } from "react";
import Queue from "@common/queue";

interface I_Result {
  return_code: string;
  return_msg: string;
}

interface I_LivenssResult {
  confidence: number;
  is_live: boolean;
  return_msg: I_Result;
}

interface I_CompareResult {
  match_result: number;
  similarity_confidence: number;
  return_msg: I_Result;
}

interface I_FaceContext {
  config: I_FaceConfig;
  livenessQueue: Queue;
  result: { liveness: I_LivenssResult; compare: I_CompareResult };
  selfieImage: string;
  handlers: {
    reset: () => void;
    setResult: (faceResult: { liveness: I_LivenssResult; compare: I_CompareResult }) => void;
    setLivenessQueue: (images: Queue) => void;
    setSelfieImage: (selfieImage: string) => void;
  };
}

const Context = createContext<I_FaceContext | null>(null);

export const useFaceContext = () => {
  const context = useContext(Context);
  if (!context) throw Error("ocr context is null");
  return context;
};

interface I_FaceConfig {
  livenessCount: number;
  mirrorMode: boolean;
  validation: boolean;
  box: { color: string; borderSize: number };
  useValidateMessage: boolean;
  backgroundColor: string;
  circleStrokeWidth: number;
}

const config: I_FaceConfig = {
  livenessCount: 4,
  mirrorMode: true,
  validation: true,
  box: { color: "blue", borderSize: 2 },
  useValidateMessage: false,
  backgroundColor: "#000000aa",
  circleStrokeWidth: 2,
};

const defaultContextValue = {
  config,
  livenessQueue: new Queue(),
  result: {
    liveness: {} as I_LivenssResult,
    compare: {} as I_CompareResult,
  },
  selfieImage: "",
};

const Provider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState(defaultContextValue);

  const setResult = (result: { liveness: I_LivenssResult; compare: I_CompareResult }) => {
    setState((prev) => ({ ...prev, result }));
  };

  const setLivenessQueue = (images: Queue) => {
    setState((prev) => ({ ...prev, livenessQueue: images }));
  };

  const setSelfieImage = (selfieImage: string) => {
    setState((prev) => ({ ...prev, selfieImage }));
  };

  const reset = () => {
    setState(defaultContextValue);
  };

  const handlers = { reset, setResult, setLivenessQueue, setSelfieImage };

  return <Context.Provider value={{ ...state, handlers }}>{children}</Context.Provider>;
};

export default { Context, Provider };
